import type { II18nConfiguration } from "@bbj-types/organisation";
import { FormControl, FormHelperText, MenuItem, Select, Stack, TextField, type TextFieldProps } from "@mui/material";
import { useMultiRefs } from "@root/utils/hooks/useMultiRef";
import { useI18nContext } from "@root/utils/organisation";
import { PhoneNumberFormat } from "google-libphonenumber";
import { type ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  availableRegionCodes,
  caretIndexAfterDigits,
  countDigits,
  getDialCode,
  phoneCodeLabelMap,
  phoneUtil,
} from "./utils";

interface IPhoneInputV2Props extends Omit<TextFieldProps, "value"> {
  value?: string;
}

export const PhoneInputV2 = ({ value, onChange, helperText, error, inputRef, ...props }: IPhoneInputV2Props) => {
  const { phoneCode } = useI18nContext();
  const [selectedRegionCode, setSelectedRegionCode] = useState<II18nConfiguration["phoneCode"]>(phoneCode);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const pendingCaretDigitsRef = useRef<number | null>(null);
  const multiInputRef = useMultiRefs(internalInputRef, inputRef);

  useEffect(() => {
    if (value) {
      try {
        const region = phoneUtil.getRegionCodeForNumber(
          phoneUtil.parse(value, "ZZ")
        ) as II18nConfiguration["phoneCode"];
        if (region && availableRegionCodes.includes(region)) {
          setSelectedRegionCode(region);
        }
      } catch {
        // incomplete number, keep current region
      }
    }
  }, [value]);

  const dialCodeRegex = useMemo(
    () => new RegExp(`^\\+${phoneUtil.getCountryCodeForRegion(selectedRegionCode)} ?`),
    [selectedRegionCode]
  );

  const displayValue = useMemo(() => {
    if (!value) {
      return value;
    }
    try {
      return phoneUtil
        .format(phoneUtil.parse(value, selectedRegionCode), PhoneNumberFormat.INTERNATIONAL)
        .replace(dialCodeRegex, "");
    } catch {
      return value.replace(dialCodeRegex, "");
    }
  }, [value, selectedRegionCode, dialCodeRegex]);

  useLayoutEffect(() => {
    if (pendingCaretDigitsRef.current === null || !internalInputRef.current) {
      return;
    }
    const caretIndex = caretIndexAfterDigits(displayValue ?? "", pendingCaretDigitsRef.current);
    internalInputRef.current.setSelectionRange(caretIndex, caretIndex);
    pendingCaretDigitsRef.current = null;
  }, [displayValue]);

  const handleChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      regionCode: II18nConfiguration["phoneCode"] = selectedRegionCode
    ) => {
      if (!onChange) {
        return;
      }
      const caretPosition = event.target.selectionStart;
      pendingCaretDigitsRef.current =
        caretPosition == null ? event.target.value.length : countDigits(event.target.value, caretPosition);
      const newEvent = { ...event };
      newEvent.target.value = event.target.value ? `${getDialCode(regionCode)}${event.target.value}` : "";
      onChange(newEvent);
    },
    [onChange, selectedRegionCode]
  );

  return (
    <FormControl error={error} className={props.className} fullWidth={props.fullWidth}>
      <Stack direction="row">
        <Select
          size="small"
          value={selectedRegionCode}
          onChange={(event) => {
            const newRegion = event.target.value as II18nConfiguration["phoneCode"];
            setSelectedRegionCode(newRegion);
            if (value) {
              handleChange(
                { target: { value: displayValue ?? "" } } as unknown as ChangeEvent<HTMLInputElement>,
                newRegion
              );
            }
          }}
          renderValue={(value) => getDialCode(value)}
          sx={{ minWidth: 85 }}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            slotProps: {
              paper: {
                sx: { boxShadow: 0, filter: "none", border: "1px solid", borderColor: "border.main" },
              },
            },
          }}
        >
          {availableRegionCodes.map((regionCode) => (
            <MenuItem key={regionCode} value={regionCode}>
              {getDialCode(regionCode)} ({phoneCodeLabelMap[regionCode]})
            </MenuItem>
          ))}
        </Select>
        <TextField {...props} error={error} value={displayValue} onChange={handleChange} inputRef={multiInputRef} />
      </Stack>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
